import { useEffect, useState, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { getProfileFromUsername } from '@/api/profile';
import authSlice from '@/store/slices/auth';
import axiosService from '@/utils/axios';
import { RootState } from '@/store';
import '@/components/brand/JWCarousel/styles/highlights.css';
import Highlights from '@/components/Profile/Highlights';
import { HighlightType } from '@/types/profile';
import { appendHighlightsFormFields } from '@/utils/highlightFormData';
import { mergeSavedHighlightImages } from '@/utils/highlightIdentity';
import { JWButton } from '@/components/brand';
import { ProfileImage, CoverImage } from '@/components/Profile';
import { monthNames } from '@/utils/user';
import { PROFILE_MODULE_CLASS } from '@/theme/surfaceClasses';
import { Checkmark, PencilEditIcon, EyeIcon } from '@/components/icons';
import Skills from '@/components/Profile/Skills';
import Headline from '@/components/Profile/Headline';
import Influences from '@/components/Profile/Influences';
import SocialLinks from '@/components/Profile/SocialLinks';
import SimilarArtists from '@/components/Profile/SimilarArtists';
import LocationSection from '@/components/Profile/LocationSection/LocationSection';
import EditProfileModal from '@/components/modals/EditProfileModal';
import ProfileDiscoverableModal from '@/components/modals/ProfileDiscoverableModal';
import ViewProfileAsOthersModal from '@/components/modals/ViewProfileAsOthersModal';
import { SpotifyArtistType } from '@/types/spotify';
import EventSection from '@/components/Profile/EventSection';
import PlusIcon from '@/components/icons/PlusIcon';
import { SkillType } from '@/types/profile';
import { isDiscoverable } from '@/utils/profileUtil';
import { imageUrlAfterProfileSave } from '@/utils/imageUrlAfterProfileSave';
import { ProfileResponse } from '@/utils/types';
import {
    ProfileInfoType,
    UserInfoType,
    ImageType,
    LocationType,
    ProfileProps,
} from '@/types/profile';
import CollabButton from '@/components/Profile/CollabButton/CollabButton';
import LikeButton from '@/components/Profile/LikeButton';
import { GuestGateDialog, guestGatePrompts, useGuestGate } from '@/components/auth/GuestGate';
import { Users } from 'lucide-react'; // Import the Users icon from Lucide
import { likeUser, unlikeUser } from '@/api/user';
import { applyLikedUser, useLikedUserSet } from '@/hooks/likedUsers';
import Loader from '@/components/ui/loader';
import NotFound404 from '@/pages/NotFound404';
import { sendCollabRequest, checkCollabStatus, CollabEntry } from '@/api/collaborations';
import { profileRedirectTarget } from '@/pages/Onboarding/onboardingLogic';
import { PlaceholderLabel } from '@/components/Profile/PlaceholderLabel';
import { ProfileFieldHint } from '@/components/Profile/ProfileHint';
import { useProfileHints } from '@/components/Profile/useProfileHints';
import { getUserDisplayName } from '@/utils/userDisplay';
import { usePageMeta } from '@/hooks/usePageMeta';
import { profileDocumentTitle } from '@/utils/documentTitle';
import { imageAlt, metaDescription } from '@/utils/seoSlug';
import { fetchJamBoardPinsByAuthorUserId } from '@/api/jamboard';
import { JamBoardPin } from '@/types/jamboard';

const Profile = ({ testing = false }: ProfileProps) => {
    const { username } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Get current logged in user from Redux
    const currentUser = useSelector((state: RootState) => state.auth.user);
    // Collab and Like are member actions. A visitor is asked to create an account instead.
    const { requireAccount, gateProps } = useGuestGate();

    // Handle fresh navigation - scroll to top when 'fresh=true' is in the URL
    useEffect(() => {
        const isFreshNavigation = searchParams.get('fresh') === 'true';
        if (isFreshNavigation) {
            // The page scrolls inside the layout, not the window.
            window.scrollTo(0, 0);
            document.getElementById('app-scroll-container')?.scrollTo(0, 0);

            // Remove the query parameter from URL without page reload
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('fresh');
            const newUrl = location.pathname + (newSearchParams.toString() ? `?${newSearchParams.toString()}` : '');
            window.history.replaceState({}, '', newUrl);
        }
    }, [location, searchParams]);

    // Scroll to top when username changes (new profile is viewed)
    useEffect(() => {
        window.scrollTo(0, 0);
        document.getElementById('app-scroll-container')?.scrollTo(0, 0);
    }, [username]);

    // Array of fun loading messages
    const loadingMessages = [
        'Tuning the instruments...',
        'Setting the stage...',
        'Warming up the vocals...',
        'Dropping the beat...',
        'Cueing the spotlight...'
    ];

    // Select a random loading message
    const randomLoadingMessage = useMemo(() => {
        const randomIndex = Math.floor(Math.random() * loadingMessages.length);
        return loadingMessages[randomIndex];
    }, []);

    const [profileInfo, setProfileInfo] = useState<ProfileInfoType>();
    const [canEdit, setCanEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<'not-found' | 'failed' | null>(null);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [profileSaved, setProfileSaved] = useState(false);
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    const bioTextRef = useRef<HTMLParagraphElement>(null);
    const queryClient = useQueryClient();
    const likedUserIds = useLikedUserSet();

    // Clean up the profile fetch useEffect
    useEffect(() => {
        let cancelled = false;
        const fetchProfile = async () => {
            if (!username) {
                setLoadError('not-found');
                setIsLoading(false);
                return;
            }

            try {
                const data = await getProfileFromUsername(username);
                if (cancelled) return;
                const isOwnProfile = currentUser?.username === data.userData?.username;

                // Always update local state with the fetched profile data
                setProfileInfo(data as unknown as ProfileInfoType);
                rememberSavedProfile(data as unknown as ProfileInfoType);
                setCanEdit(isOwnProfile);

                if (Array.isArray(data.jamBoardPins)) {
                    setActiveJamBoardPins(data.jamBoardPins);
                } else if (data.userData?._id) {
                    const pins = await fetchJamBoardPinsByAuthorUserId(
                        String(data.userData._id)
                    );
                    if (cancelled) return;
                    setActiveJamBoardPins(pins);
                } else {
                    setActiveJamBoardPins([]);
                }

                // Only update Redux state if viewing own profile
                if (isOwnProfile) {
                    dispatch(authSlice.actions.setProfile(data));
                }

                setIsLoading(false);
            } catch (err) {
                if (cancelled) return;
                console.error('Profile not found', err);
                // Stay on the requested URL: a missing profile shows the 404 page in place,
                // while a network or server failure offers a retry instead of a false "not found".
                const status = (err as { response?: { status?: number } })?.response?.status;
                setLoadError(status === 404 ? 'not-found' : 'failed');
                setIsLoading(false);
            }
        };

        setLoadError(null);
        fetchProfile();
        return () => {
            cancelled = true;
        };
    }, [username, currentUser?.username, loadAttempt]);

    // Remove the auto-save effect that was causing infinite loops
    useEffect(() => {
        if (username === currentUser?.username) {
            setCanEdit(true);
        }
    }, [currentUser?.username, username]);

    // Replace the display name logic with our utility function
    const displayName = useMemo(() =>
        getUserDisplayName({ profileInfo }),
    [profileInfo]);

    const portraitAlt = useMemo(
        () => imageAlt(displayName, profileInfo?.skills, profileInfo?.location?.city),
        [displayName, profileInfo?.skills, profileInfo?.location?.city],
    );

    const seoQuery = useQuery({
        queryKey: ['profile-seo', username],
        enabled: Boolean(username && username !== '404'),
        retry: false,
        queryFn: () => axiosService
            .get(`/seo/profile/${encodeURIComponent(username || '')}`)
            .then((response) => response.data),
    });

    const pageMeta = useMemo(() => {
        if (!username || username === '404') return null;
        const seo = seoQuery.data;
        if (seo?.documentTitle && seo.username === username.toLowerCase()) return seo;
        if (!profileInfo) return null;
        const unlisted = profileInfo.hidden === true || profileInfo.isDiscoverable === false;
        return {
            documentTitle: profileDocumentTitle({
                displayName: displayName || username,
                headline: profileInfo.headline,
                city: profileInfo.location?.city,
                skills: profileInfo.skills,
            }),
            metaDescription: metaDescription({
                displayName: displayName || username,
                skills: profileInfo.skills,
                city: profileInfo.location?.city,
                description: profileInfo.description,
            }),
            canonicalUrl: `${window.location.origin}/${username.toLowerCase()}`,
            robots: unlisted ? 'noindex, follow' : undefined,
            username,
            imageAlt: portraitAlt,
        };
    }, [username, seoQuery.data, profileInfo, displayName, portraitAlt]);

    usePageMeta(pageMeta);

    const [allSkillsWereRemoved, setAllSkillsWereRemoved] = useState(false);

    const [profileImage, setProfileImage] = useState<ImageType>({
        url: null,
        file: null,
    });
    const [coverImage, setCoverImage] = useState<ImageType>({
        url: null,
        file: null,
    });

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [showDiscoverableModal, setShowDiscoverableModal] =
        useState<boolean>(false);
    const [isEditingPictures, setIsEditingPictures] = useState<boolean>(false);
    const [isEditingDescription, setIsEditingDescription] =
        useState<boolean>(false);
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const [collabStatus, setCollabStatus] = useState<'none' | 'pending' | 'accepted'>('none');
    const [initiatedByOtherUser, setInitiatedByOtherUser] = useState(false);
    const [pendingCollabId, setPendingCollabId] = useState<string | undefined>(undefined);
    const [collabHistory, setCollabHistory] = useState<CollabEntry[]>([]);

    const [showViewAsOthersModal, setShowViewAsOthersModal] = useState(false);
    const [activeJamBoardPins, setActiveJamBoardPins] = useState<JamBoardPin[]>([]);
    const [viewedUsername, setViewedUsername] = useState(username);
    if (username !== viewedUsername) {
        setViewedUsername(username);
        setProfileInfo(undefined);
        setIsLoading(true);
        setLoadError(null);
        setCanEdit(false);
        setCollabStatus('none');
        setInitiatedByOtherUser(false);
        setPendingCollabId(undefined);
        setCollabHistory([]);
        setActiveJamBoardPins([]);
        setProfileImage({ url: null, file: null });
        setCoverImage({ url: null, file: null });
    }
    const latestProfileSaveRequestRef = useRef(0);
    const lastSavedProfileSnapshotRef = useRef<Record<string, string>>({});
    const profileInfoRef = useRef(profileInfo);
    const saveChainRef = useRef(Promise.resolve());
    profileInfoRef.current = profileInfo;

    useEffect(() => {
        if (!username && currentUser) {
            // Redirect to /<username> if we're at /profile
            navigate(`/${currentUser.username}`, { replace: true });
        }
    }, [username, currentUser, navigate]);

    // Finished onboarding stays on the profile even when the profile is empty.
    useEffect(() => {
        if (!canEdit || !currentUser) return;
        if (currentUser.onboardingCompletedAt) return;
        let cancelled = false;
        axiosService.get('/user/onboarding').then((response) => {
            if (cancelled) return;
            const target = profileRedirectTarget({
                canEdit: true,
                onboardingCompletedAt: response.data?.onboardingCompletedAt,
                serverNeedsOnboarding: !!response.data?.needsOnboarding,
            });
            if (response.data?.onboardingCompletedAt) {
                dispatch(authSlice.actions.setUser({
                    ...currentUser,
                    onboardingCompletedAt: response.data.onboardingCompletedAt,
                    onboardingIdentity: response.data.identity || currentUser.onboardingIdentity,
                }));
            }
            if (target) navigate(target, { replace: true });
        }).catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [canEdit, currentUser, dispatch, navigate]);

    // Update the checkStatus function in the useEffect
    useEffect(() => {
        const checkStatus = async () => {
            // Only run this check when viewing another user's profile
            if (profileInfo?.userData?._id && currentUser && profileInfo.userData._id !== currentUser._id) {
                try {
                    const statusResponse = await checkCollabStatus(profileInfo.userData._id);
                    setCollabStatus(statusResponse.status);
                    setPendingCollabId(statusResponse.pendingCollabId);
                    setCollabHistory(statusResponse.collaborations || []);

                    // Check if the other user initiated a pending generic collab request
                    if (statusResponse.status === 'pending' && statusResponse.initiatedBy) {
                        const isInitiatedByOther = statusResponse.initiatedBy.toString() === profileInfo.userData._id.toString();
                        setInitiatedByOtherUser(isInitiatedByOther);
                    } else {
                        setInitiatedByOtherUser(false);
                    }
                } catch (error) {
                    console.error('Error checking collab status:', error);
                    setCollabStatus('none');
                    setInitiatedByOtherUser(false);
                    setPendingCollabId(undefined);
                    setCollabHistory([]);
                }
            }
        };

        checkStatus();
    }, [profileInfo?.userData?._id, currentUser]);

    const handleCollabRequestSent = () => {
        setCollabStatus('pending');
    };

    // Get the current user's ID from Redux store
    const currentUserId = useSelector(
        (state: RootState) => state.auth.user?._id
    );

    const profileHintSource = useMemo(() => {
        if (!canEdit || !profileInfo) return null;
        return {
            profilePhoto: profileImage.url || profileInfo.profilePhoto,
            headline: profileInfo.headline,
            location: profileInfo.location,
        };
    }, [
        canEdit,
        profileImage.url,
        profileInfo,
    ]);

    const profileHints = useProfileHints(canEdit ? currentUserId : null, profileHintSource);

    // Re-fetch collab status when a collaboration is ended from the info modal
    const handleCollabEnded = async () => {
        if (!profileInfo?.userData?._id || !currentUser) return;
        try {
            const statusResponse = await checkCollabStatus(profileInfo.userData._id);
            setCollabStatus(statusResponse.status);
            setPendingCollabId(statusResponse.pendingCollabId);
            setCollabHistory(statusResponse.collaborations || []);
            if (statusResponse.status === 'pending' && statusResponse.initiatedBy) {
                setInitiatedByOtherUser(statusResponse.initiatedBy.toString() === profileInfo.userData._id.toString());
            } else {
                setInitiatedByOtherUser(false);
            }
        } catch {
            setCollabStatus('none');
            setCollabHistory([]);
        }
    };

    const handleInputChange = (update: Partial<ProfileInfoType>) => {
        setProfileInfo((current) => (current ? { ...current, ...update } : current));
    };

    const handleImageUpload = (fList: FileList | null, type: string) => {
        const file = fList?.[0];
        if (file) {
            // The file validation is now handled in the Profile components
            // and the image editor modal handles the editing

            const tmpUrl = file ? URL.createObjectURL(file) : null;
            if (type == 'profile') {
                setProfileImage({ url: tmpUrl, file: file });
            } else {
                setCoverImage({ url: tmpUrl, file: file });
            }
            setIsEditingPictures(true);
        }
    };

    useEffect(() => {
        if (isEditingPictures && !testing) {
            onProfileSave();
            setIsEditingPictures(false);
        }
    }, [isEditingPictures]);

    const displayCreationDate = useMemo(() => {
        const creationDate = profileInfo?.createdAt;
        if (creationDate) {
            const date = new Date(creationDate);
            return `${monthNames[date.getMonth()]}, ${date.getFullYear()}`;
        }
        return '';
    }, [profileInfo?.createdAt]);

    const editDescriptionClick = () => {
        setIsEditingDescription(true);
        // Use setTimeout to ensure the textarea is rendered before focusing
        setTimeout(() => {
            if (descriptionRef?.current) {
                descriptionRef.current.focus();
            }
        }, 0);
    };

    const confirmDescription = () => {
        setIsEditingDescription(false);
        onProfileSave();
    };

    const handleDescriptionChange = (val: any) => {
        setIsEditingDescription(true);
        handleInputChange({
            description: val as string | undefined,
        });
    };

    const setSelectedSkills = (newSkills: Array<SkillType>) => {
        // Check if all skills were removed (setting to empty array)
        if (newSkills.length === 0) {
            setAllSkillsWereRemoved(true);
        }

        if (profileInfo) {
            setProfileInfo({ ...profileInfo, skills: newSkills });
        }
    };

    /** Keys merged onto the profile client-side that must never be written by a profile save. */
    const PROFILE_FORM_SKIP_KEYS = new Set([
        'events',
        'jamBoardPins',
        'userData',
        'similarAttributes',
        'isCurrentlyBoosted',
        'boostExpiresAt',
        'boostType',
        '_id',
        'user',
        'userId',
        'username',
        'email',
        'firstName',
        'lastName',
        'createdAt',
        'updatedAt',
        'collaborations',
        'viewCount',
        'hidden',
        'verified',
        'lastActive',
    ]);

    const rememberSavedProfile = (profile: object | null | undefined) => {
        const next: Record<string, string> = {};
        if (profile) {
            Object.entries(profile).forEach(([key, value]) => {
                next[key] = JSON.stringify(value ?? null);
            });
        }
        lastSavedProfileSnapshotRef.current = next;
    };

    const handleInfluencesSaved = (update: { favArtists: SpotifyArtistType[]; favGenres: string[] }) => {
        setProfileInfo((prev) => (
            prev ? { ...prev, favArtists: update.favArtists, favGenres: update.favGenres } : prev
        ));
        lastSavedProfileSnapshotRef.current = {
            ...lastSavedProfileSnapshotRef.current,
            favArtists: JSON.stringify(update.favArtists ?? null),
            favGenres: JSON.stringify(update.favGenres ?? null),
        };
    };

    const getFormData = (profileOverride?: ProfileInfoType) => {
        const formData = new FormData();
        const savedSnapshot = lastSavedProfileSnapshotRef.current;
        const profile = profileOverride ?? profileInfoRef.current;
        if (profile) {
            (Object.keys(profile) as (keyof ProfileInfoType)[]).forEach((key) => {
                if (PROFILE_FORM_SKIP_KEYS.has(key)) {
                    return;
                }
                const value = profile[key];
                if (value !== null) {
                    const serialized = JSON.stringify(value ?? null);
                    if (savedSnapshot[key] !== undefined && savedSnapshot[key] === serialized) {
                        return;
                    }
                    if (key === 'highlights') {
                        appendHighlightsFormFields(formData, profile.highlights);
                    } else if (
                        key === 'skills' ||
                        key === 'favArtists' ||
                        key === 'favGenres' ||
                        key === 'location' ||
                        key === 'hiddenJamBoardPinIds'
                    ) {
                        formData.append(key, JSON.stringify(value));
                    } else if (key === 'profilePhoto' || key === 'coverPhoto') {
                        // do nothing
                    } else {
                        formData.append(key, typeof value === 'string' ? value : String(value));
                    }
                }
            });
        }

        if (profileImage?.file) {
            formData.set('profilePhoto', profileImage.file);
        }

        if (coverImage?.file) {
            formData.set('coverPhoto', coverImage.file);
        }

        return formData;
    };

    const updateProfileData = async (
        profileId: string,
        userId: string,
        requestId?: number,
        profileSnapshot?: ProfileInfoType
    ) => {
        try {
            // Properly set up headers for multipart form data when uploading files
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            };
            const formData = getFormData(profileSnapshot);

            const { data } = await axiosService.put(
                `/profile/${profileId}/?userId=${userId}`,
                formData,
                config
            );

            const isLatestRequest =
                requestId === undefined ||
                requestId === latestProfileSaveRequestRef.current;

            // Only apply response data if this is the latest save request.
            // This prevents older async responses from overwriting newer UI state.
            if (!isLatestRequest) {
                return data;
            }

            const savedForClient = {
                ...data,
                coverPhoto: imageUrlAfterProfileSave(data.coverPhoto, profileInfoRef.current?.coverPhoto) || null,
                profilePhoto: imageUrlAfterProfileSave(data.profilePhoto, profileInfoRef.current?.profilePhoto) || null,
            };

            setCoverImage((current) => ({
                url: imageUrlAfterProfileSave(data.coverPhoto, current.url),
                file: null,
            }));

            setProfileImage((current) => ({
                url: imageUrlAfterProfileSave(data.profilePhoto, current.url),
                file: null,
            }));

            if (data.profilePhoto && userId) {
                localStorage.setItem(
                    `userProfilePhoto_${userId}`,
                    JSON.stringify({
                        photoUrl: data.profilePhoto,
                        timestamp: Date.now()
                    })
                );
            }

            if (canEdit) {
                setProfileInfo((prevProfileInfo) => {
                    if (!prevProfileInfo) {
                        rememberSavedProfile(savedForClient);
                        return savedForClient;
                    }
                    const nextDisplayName =
                        savedForClient.displayName || prevProfileInfo.displayName;
                    const merged = {
                        ...prevProfileInfo,
                        ...savedForClient,
                        displayName: nextDisplayName,
                        coverPhoto: savedForClient.coverPhoto || prevProfileInfo.coverPhoto,
                        profilePhoto: savedForClient.profilePhoto || prevProfileInfo.profilePhoto,
                        highlights: mergeSavedHighlightImages(
                            prevProfileInfo.highlights,
                            savedForClient.highlights
                        ) ?? prevProfileInfo.highlights,
                    };
                    if (prevProfileInfo.userData) {
                        merged.userData = {
                            ...prevProfileInfo.userData,
                            ...(typeof savedForClient.userData === 'object' && savedForClient.userData
                                ? savedForClient.userData
                                : {}),
                            displayName: nextDisplayName,
                        };
                    }
                    rememberSavedProfile(merged);
                    return merged;
                });
                dispatch(authSlice.actions.setProfile(savedForClient));
            } else {
                setProfileInfo(savedForClient);
                rememberSavedProfile(savedForClient);
            }

            return data;
        } catch (error) {
            console.error('Error updating profile data:', error);
            throw error;  // Re-throw to be handled by the caller
        }
    };

    const onProfileSave = (snapshot?: ProfileInfoType) => {
        setIsEditing(false);
        if (!currentUser) return Promise.resolve();

        if (snapshot) {
            profileInfoRef.current = snapshot;
        }

        // Bump before any await so an in-flight response cannot land as the latest
        // save. Image signing is slow, and that response used to restore a highlight
        // the user had already deleted.
        const requestId = latestProfileSaveRequestRef.current + 1;
        latestProfileSaveRequestRef.current = requestId;
        const profileForRequest = snapshot ?? profileInfoRef.current;
        const skillsCountBefore = profileForRequest?.skills?.length || 0;
        const profileId = profileForRequest?._id || currentUser.profileId;
        const userId = currentUser._id;

        const job = saveChainRef.current.then(async () => {
            if (requestId !== latestProfileSaveRequestRef.current) return;
            if (!profileId) return;
            try {
                const updatedProfileData = await updateProfileData(
                    profileId,
                    userId,
                    requestId,
                    profileForRequest
                );
                if (requestId !== latestProfileSaveRequestRef.current) return;

                await updateUserData();

                const skillsCountAfter = updatedProfileData?.skills?.length || 0;
                if ((skillsCountBefore === 0 && skillsCountAfter > 0) ||
                    (allSkillsWereRemoved && skillsCountAfter > 0)) {
                    setShowDiscoverableModal(true);
                    setAllSkillsWereRemoved(false);
                }

                setProfileSaved(true);
            } catch (error) {
                console.error('Error saving profile:', error);
            }
        });
        saveChainRef.current = job;
        return job;
    };

    // Remove the auto-save effect
    useEffect(() => {
        if (!isEditing) {
            setCoverImage((current) => ({
                url: imageUrlAfterProfileSave(profileInfo?.coverPhoto, current.url),
                file: null,
            }));
            setProfileImage((current) => ({
                url: imageUrlAfterProfileSave(profileInfo?.profilePhoto, current.url),
                file: null,
            }));
        }
    }, [profileInfo?.coverPhoto, profileInfo?.profilePhoto, isEditing]);

    // Initialize allSkillsWereRemoved based on initial profile data
    useEffect(() => {
        if (profileInfo && !isLoading) {
            // If profile has no skills on initial load, set the flag to true
            if (!profileInfo.skills || profileInfo.skills.length === 0) {
                setAllSkillsWereRemoved(true);
            }
        }
    }, [profileInfo, isLoading]);

    // Update user data function simplified
    const updateUserData = async () => {
        if (!currentUser?._id || !profileInfo?.userData) {
            return;
        }
        try {
            const response = await axiosService.patch(`/user/${currentUser._id}`, {
                displayName: profileInfo.displayName,
                firstName: profileInfo.userData.firstName,
                lastName: profileInfo.userData.lastName,
            });
            if (response.data && canEdit) {
                dispatch(authSlice.actions.setUser(response.data));
            }
        } catch (error) {
            console.error('Error updating user data:', error);
        }
    };

    // Check if text is truncated
    useEffect(() => {
        const checkTextTruncation = () => {
            if (bioTextRef.current) {
                const isTruncated = bioTextRef.current.scrollHeight > bioTextRef.current.clientHeight;
                setIsTextTruncated(isTruncated);
            }
        };

        checkTextTruncation();
        // Re-check on window resize
        window.addEventListener('resize', checkTextTruncation);
        return () => window.removeEventListener('resize', checkTextTruncation);
    }, [profileInfo?.description]);

    const profileUserId = profileInfo?.userData?._id;
    const likeStatus = Boolean(profileUserId && likedUserIds.has(String(profileUserId)));

    const handleLike = async () => {
        if (requireAccount(guestGatePrompts.like(displayName))) return;
        if (!profileUserId || canEdit) return;

        try {
            const response = likeStatus
                ? await unlikeUser(profileUserId)
                : await likeUser(profileUserId);

            await applyLikedUser(queryClient, profileUserId, response.liked);
        } catch (error) {
            console.error('Error toggling like:', error);
        }
    };

    // If still loading or no profile data, show loading state
    if (loadError === 'not-found') {
        return <NotFound404 username={username} />;
    }

    if (loadError === 'failed') {
        return (
            <NotFound404
                variant="error"
                onRetry={() => setLoadAttempt((attempt) => attempt + 1)}
            />
        );
    }

    if (isLoading || !profileInfo) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-screen">
                <Loader size="lg" text={randomLoadingMessage} />
            </div>
        );
    }

    return (
        <div className="flex-1">
            <div className="flex flex-col w-full bg-neutral-100 pb-4">
                <div className="flex flex-col w-full px-3 space-y-4">
                    <CoverImage
                        testId="cover-photo"
                        onChange={handleImageUpload}
                        src={coverImage?.url || undefined}
                        canEdit={canEdit}
                        alt={portraitAlt ? `${portraitAlt} cover photo` : ''}
                    />
                    <div className={`${PROFILE_MODULE_CLASS} md:p-2`}>
                        <div className="flex flex-col justify-center space-y-4 h-full">
                            <div className="flex pt-2 pb-3 h-full md:h-auto md:items-stretch">
                                <div className="flex flex-col w-full h-full md:h-auto lg:!w-1/2">
                                    <div className="flex w-full h-full md:h-auto md:min-h-[227px] gap-2 md:items-stretch">
                                        <div className="flex flex-col w-1/2 h-full md:h-[227px] md:max-h-[227px] md:flex-none md:self-start items-center justify-center md:relative md:translate-y-[-40px]">
                                            <div
                                                className="flex flex-row justify-center"
                                                style={{
                                                    position: 'relative',
                                                    ...(canEdit && {
                                                        right: '-30px',
                                                    }),
                                                    ...(!canEdit && {
                                                        right: '-12px',
                                                    }),
                                                }}
                                            >
                                                <ProfileFieldHint id="profile-photo" active={profileHints.isActive('profile-photo')} onDismiss={() => profileHints.dismiss('profile-photo')}>
                                                    <ProfileImage
                                                        testId="profile-photo"
                                                        src={profileImage?.url || undefined}
                                                        onChange={handleImageUpload}
                                                        canEdit={canEdit}
                                                        createdAt={profileInfo?.createdAt || undefined}
                                                        isBoosted={profileInfo?.isCurrentlyBoosted}
                                                        alt={portraitAlt}
                                                    />
                                                </ProfileFieldHint>
                                            </div>
                                            <h1 className="m-0 text-xl text-center font-bold leading-6 min-h-6">
                                                {displayName}
                                            </h1>
                                            <div className="flex flex-row justify-center pt-2 px-2">
                                                <SocialLinks
                                                    profileInfo={profileInfo}
                                                    setIsEditing={setIsEditing}
                                                    canEdit={canEdit}
                                                />
                                            </div>
                                            <div className="flex flex-col top-full justify-center gap-2">
                                                <p className="text-center text-sm text-gray-400">
                                                    Member Since {displayCreationDate}
                                                </p>
                                            </div>
                                            <div className="mt-2">
                                                {canEdit ? (
                                                    <div className="flex gap-2 justify-center">
                                                        <ProfileFieldHint id="location" active={profileHints.isActive('location')} onDismiss={() => profileHints.dismiss('location')}>
                                                            <JWButton
                                                                className="flex items-center justify-center gap-2 transition-transform hover:scale-105"
                                                                onClick={() => setIsEditing(true)}
                                                            >
                                                                Edit Profile
                                                            </JWButton>
                                                        </ProfileFieldHint>
                                                        <button
                                                            className="flex items-center justify-center p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                                                            onClick={() => setShowViewAsOthersModal(true)}
                                                            title="View profile as others see it"
                                                        >
                                                            <EyeIcon width={20} height={20} fill="#626262" fillOpacity={0.8} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-2 justify-center">
                                                        <CollabButton
                                                            toUserId={profileInfo.userData?._id ?? ''}
                                                            initialCollabStatus={collabStatus}
                                                            initiatedByOtherUser={initiatedByOtherUser}
                                                            pendingCollabId={pendingCollabId}
                                                            collabHistory={collabHistory}
                                                            icon={<Users size={18} />}
                                                            onRequestSent={handleCollabRequestSent}
                                                            onCollabEnded={handleCollabEnded}
                                                            collaboratorInfo={profileInfo}
                                                            interceptRequest={() => requireAccount(guestGatePrompts.collab(displayName))}
                                                        />
                                                        <LikeButton
                                                            handleLike={handleLike}
                                                            likeStatus={likeStatus}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            {profileInfo.location && (
                                                <div className="hidden md:flex">
                                                    <LocationSection
                                                        profileInfo={
                                                            profileInfo
                                                        }
                                                    />
                                                </div>
                                            )}

                                            {profileInfo?.location && (
                                                <div className="flex md:hidden mt-2">
                                                    <LocationSection profileInfo={profileInfo} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col w-1/2 md:min-h-[227px] md:self-stretch">
                                            <div className="flex flex-col gap-2 w-full h-full">
                                                <div className="flex justify-center md:justify-start w-full">
                                                    <div className='w-full mb-2'>
                                                        <Headline
                                                            profileData={profileInfo}
                                                            canEdit={canEdit}
                                                            hintActive={profileHints.isActive('headline')}
                                                            onDismissHint={() => profileHints.dismiss('headline')}
                                                            onHeadlineSaved={(headline) => {
                                                                setProfileInfo((current) => current ? { ...current, headline } : current);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                {!profileInfo?.description &&
                                                canEdit &&
                                                !isEditingDescription ? (
                                                        <div className="relative flex-grow min-h-0 h-full flex flex-col">
                                                            <button
                                                                className="group flex justify-center items-center bg-blue-200 rounded-lg pt-2 pb-2 pl-2 mr-3 sm:mr-3 md:mr-0 flex-col flex-grow h-full transition-colors hover:bg-blue-300"
                                                                style={{
                                                                    paddingRight: '2px',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    minHeight: '150px',
                                                                    maxHeight: canEdit ? 'none' : 'calc(100% - 4px)'
                                                                }}
                                                                onClick={editDescriptionClick}
                                                                aria-label="Add a bio. What makes you stand out?"
                                                            >
                                                                <PlusIcon />
                                                                <PlaceholderLabel className="text-md" title="Add a bio" benefit="What makes you stand out?" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="relative flex-grow min-h-0 h-full flex flex-col">
                                                            <div
                                                                className={`rounded-lg bg-gray-200 pt-2 pb-2 pl-2 mr-3 sm:mr-3 md:mr-0 flex flex-col flex-grow h-full dark:bg-jw-surface-3 ${canEdit && !isEditingDescription ? 'hover:bg-gray-300 relative group' : ''}`}
                                                                style={{
                                                                    paddingRight: '2px',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    cursor: canEdit && !isEditingDescription ? 'pointer' : 'default',
                                                                    transition: 'background-color 0.2s ease'
                                                                }}
                                                                onClick={(e) => {
                                                                    if (canEdit && !isEditingDescription) {
                                                                        e.stopPropagation();
                                                                        editDescriptionClick();
                                                                    }
                                                                }}
                                                            >
                                                                {canEdit && !isEditingDescription && (
                                                                    <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-60 transition-opacity">
                                                                        <PencilEditIcon
                                                                            width={16}
                                                                            height={16}
                                                                            fill={'#626262'}
                                                                        />
                                                                    </div>
                                                                )}
                                                                {!canEdit ? (
                                                                    <p
                                                                        ref={bioTextRef}
                                                                        className="text-eerieblack font-sans break-words w-full h-full flex-grow"
                                                                        style={{
                                                                            paddingRight: '12px',
                                                                            minHeight: '135px',
                                                                            maxHeight: '199px',
                                                                            overflowY: 'auto',
                                                                            scrollbarWidth: 'thin',
                                                                            scrollbarColor: 'rgba(203, 213, 224, 0.8) transparent',
                                                                        }}
                                                                    >
                                                                        {
                                                                            profileInfo?.description
                                                                        }
                                                                    </p>
                                                                ) : (
                                                                    <div
                                                                        className="flex flex-col w-full h-full flex-grow relative"
                                                                        style={{ minHeight: '135px' }}
                                                                        onClick={(e) => {
                                                                            if (canEdit && !isEditingDescription) {
                                                                                e.stopPropagation();
                                                                                editDescriptionClick();
                                                                            }
                                                                        }}
                                                                    >
                                                                        <textarea
                                                                            value={profileInfo?.description || ''}
                                                                            ref={descriptionRef}
                                                                            className="text-eerieblack font-sans w-full min-h-0 flex-grow resize-none outline-none bg-transparent pr-6"
                                                                            placeholder={!isEditingDescription ? 'Click to add a description about yourself' : 'Add a description about yourself'}
                                                                            onChange={(e) => handleDescriptionChange(e?.target?.value)}
                                                                            maxLength={255}
                                                                            onBlur={confirmDescription}
                                                                            onClick={(e) => {
                                                                                if (canEdit && !isEditingDescription) {
                                                                                    e.stopPropagation(); // Prevent double handling
                                                                                    editDescriptionClick();
                                                                                }
                                                                            }}
                                                                            autoFocus={isEditingDescription}
                                                                            style={{
                                                                                padding: '0',
                                                                                paddingRight: '24px',
                                                                                marginRight: '0px',
                                                                                overflowY: isEditingDescription ? 'auto' : 'hidden',
                                                                                scrollbarWidth: 'thin',
                                                                                scrollbarColor: 'rgba(203, 213, 224, 0.8) transparent',
                                                                                height: 'auto',
                                                                                minHeight: '140px',
                                                                                maxHeight: isEditingDescription ? '199px' : 'none',
                                                                                cursor: isEditingDescription ? 'text' : 'pointer'
                                                                            }}
                                                                        />
                                                                        {isEditingDescription ? (
                                                                            <button
                                                                                onClick={() => confirmDescription()}
                                                                                className="absolute bottom-0 right-2 opacity-60 hover:opacity-100 cursor-pointer transition-opacity"
                                                                                style={{ background: 'none' }}
                                                                            >
                                                                                <Checkmark
                                                                                    width={16}
                                                                                    height={16}
                                                                                />
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                onClick={editDescriptionClick}
                                                                                className="absolute bottom-0 right-2 opacity-60 hover:opacity-100 cursor-pointer transition-opacity"
                                                                                style={{ background: 'none' }}
                                                                            >
                                                                                <PencilEditIcon
                                                                                    width={16}
                                                                                    height={16}
                                                                                    fill={'#626262'}
                                                                                />
                                                                            </button>
                                                                        )}
                                                                        <style dangerouslySetInnerHTML={{
                                                                            __html: `
                                                                            textarea::-webkit-scrollbar {
                                                                                width: 5px;
                                                                            }
                                                                            textarea::-webkit-scrollbar-track {
                                                                                background: transparent;
                                                                            }
                                                                            textarea::-webkit-scrollbar-thumb {
                                                                                background-color: rgba(203, 213, 224, 0.8);
                                                                                border-radius: 20px;
                                                                                border: none;
                                                                            }
                                                                            textarea::-webkit-scrollbar-thumb:hover {
                                                                                background-color: rgba(160, 174, 192, 0.8);
                                                                            }
                                                                            p::-webkit-scrollbar {
                                                                                width: 5px;
                                                                            }
                                                                            p::-webkit-scrollbar-track {
                                                                                background: transparent;
                                                                            }
                                                                            p::-webkit-scrollbar-thumb {
                                                                                background-color: rgba(203, 213, 224, 0.8);
                                                                                border-radius: 20px;
                                                                                border: none;
                                                                            }
                                                                            p::-webkit-scrollbar-thumb:hover {
                                                                                background-color: rgba(160, 174, 192, 0.8);
                                                                            }
                                                                        `
                                                                        }} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-col w-1/2 mx-4 hidden md:flex self-stretch">
                                    {(profileInfo?.highlights && profileInfo.highlights.length > 0 ||
                                        canEdit) && (
                                        <div
                                            className={'highlight-container flex min-w-0 w-full flex-1 rounded-xl min-h-[227px]'}
                                        >
                                            <Highlights
                                                id={'desktop-highlights'}
                                                profileInfo={profileInfo}
                                                setProfileInfo={setProfileInfo}
                                                isLoading={isLoading}
                                                onProfileSave={onProfileSave}
                                                canEdit={canEdit}
                                                activeJamBoardPins={activeJamBoardPins}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <EditProfileModal
                        show={isEditing}
                        onClose={() => setIsEditing(false)}
                        setShow={setIsEditing}
                        profileInfo={profileInfo}
                        userInfo={profileInfo}
                        onProfileSave={onProfileSave}
                        handleInputChange={handleInputChange}
                    />
                    <ProfileDiscoverableModal
                        show={showDiscoverableModal}
                        onClose={() => setShowDiscoverableModal(false)}
                        setShow={setShowDiscoverableModal}
                        userInfo={profileInfo}
                        profileInfo={profileInfo}
                    />
                    <ViewProfileAsOthersModal
                        show={showViewAsOthersModal}
                        onClose={() => setShowViewAsOthersModal(false)}
                        setShow={setShowViewAsOthersModal}
                        userInfo={profileInfo}
                        profileInfo={profileInfo}
                    />
                    <GuestGateDialog {...gateProps} />
                    {(profileInfo?.highlights && profileInfo.highlights.length > 0 || canEdit) && (
                        <div className={`${PROFILE_MODULE_CLASS} md:hidden flex flex-col p-4 mt-4`}>
                            <div className='flex flex-row justify-between items-center mb-2'>
                                <h2 className="text-lg font-bold text-eerieblack">Highlights</h2>
                            </div>
                            <div className="h-[250px] w-full">
                                <Highlights
                                    id={'mobile-highlights'}
                                    profileInfo={profileInfo}
                                    setProfileInfo={setProfileInfo}
                                    isLoading={isLoading}
                                    onProfileSave={onProfileSave}
                                    canEdit={canEdit}
                                    mobile={true}
                                    activeJamBoardPins={activeJamBoardPins}
                                />
                            </div>
                        </div>
                    )}
                    <Skills
                        selectedSkills={profileInfo?.skills}
                        setSelectedSkills={setSelectedSkills}
                        saveSkills={onProfileSave}
                        canEdit={canEdit}
                    />
                    <Influences
                        selectedArtists={profileInfo?.favArtists || []}
                        selectedGenres={profileInfo?.favGenres || []}
                        profileId={profileInfo?._id || currentUser?.profileId}
                        onSaved={handleInfluencesSaved}
                        canEdit={canEdit}
                    />
                    <EventSection canEdit={canEdit} profileInfo={profileInfo} />

                    {!canEdit && profileInfo?.userData?._id && (
                        <SimilarArtists
                            userId={profileInfo.userData._id}
                            username={profileInfo.userData.username ?? ''}
                            displayName={displayName}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;
